import { FC, useMemo } from "react";
import TableComponent, {
  TableProps,
} from "../../../../../common/components/table/table.component";
import OrderOptimizerOptimizationStatus from "../../../../../common/services/order-optimizer/optimization-status/order-optimizer-optimization-status";
import { OrderOptimizerTaxiListingItem } from "../common/types/order-optimizer-taxi-listing-item";
import { useAppContext } from "../../../../../context/app.context";
import orderOptimizerTranslationsHelper from "../../../../../languages/order-optimizer-translations.helper";
import OrderOptimizerTaxiListingTableColumn from "../common/types/order-optimizer-taxi-listing-table-column";
import orderOptimizerTaxiListingTableHelper from "./order-optimizer-taxi-listing-table.helpers";
import useAbort from "../../../../../common/hooks/use-abort";
import OrderOptimizerOptimizationStatusParams from "../../../../../common/services/order-optimizer/optimization-status/order-optimizer-optimization-status-params";
import orderOptimizerService from "../../../../../common/services/order-optimizer/order-optimizer.service";
import orderOptimizerRoutesHelper from "../../../common/routes/order-optimizer-routes.helper";
import notificationService from "../../../../../common/utils/notification/notification.service";
import OrderOptimizerTaxiListingTableRow from "../common/types/order-optimizer-taxi-listing-table-row";
import OrderOptimizerTaxiListingTableRowComponent from "./row/order-optimizer-taxi-listing-table-row.component";

type OrderOptimizerTaxiListingTableProps = Pick<
  TableProps,
  "isError" | "isLoading"
> & {
  listingItems: OrderOptimizerTaxiListingItem[];
  selectedHumanId: number | undefined;
  applyOptimizationStatus: (
    optimizationStatus: OrderOptimizerOptimizationStatus,
    optimizationId: string,
  ) => void;
};

const OrderOptimizerTaxiListingTableComponent: FC<
  OrderOptimizerTaxiListingTableProps
> = (props) => {
  const { selectedAppLanguage, user } = useAppContext();
  const translation =
    orderOptimizerTranslationsHelper.getTaxiListingTranslations().table;

  const columns = useMemo(
    (): OrderOptimizerTaxiListingTableColumn[] =>
      orderOptimizerTaxiListingTableHelper.getColumns(),
    [],
  );

  const orderOptimizerOptimizationStatusAbort = useAbort();

  const onOptimizationClick = async (listingItemUuid: string) => {
    const optimizationStatusLoadParams: OrderOptimizerOptimizationStatusParams =
      {
        optimizationId: listingItemUuid,
      };

    await orderOptimizerService
      .getOptimizationStatus(
        optimizationStatusLoadParams,
        orderOptimizerOptimizationStatusAbort.signal,
      )
      .then((optimizationStatus) => {
        if (
          !!optimizationStatus.reservedAt &&
          !!optimizationStatus.reservedBy
        ) {
          props.applyOptimizationStatus(optimizationStatus, listingItemUuid);
        } else {
          const currentUserStatus: OrderOptimizerOptimizationStatus = {
            reservedAt: new Date(),
            reservedBy: `${user?.firstName} ${user?.lastName}`,
          };

          props.applyOptimizationStatus(currentUserStatus, listingItemUuid);
        }
      })
      .then(() => {
        window.open(
          orderOptimizerRoutesHelper.getDetailsRoute({
            id: listingItemUuid,
          }),
          "_blank",
        );
      })
      .catch(() => {
        notificationService.error(
          translation.optimizationReservationErrorNotification,
        );
      });
  };

  const createTableRow = (
    listingItem: OrderOptimizerTaxiListingItem,
  ): OrderOptimizerTaxiListingTableRow => {
    return {
      id: listingItem.id,
      value: {
        row: <></>,
      },
      hiddenComponent: (
        <OrderOptimizerTaxiListingTableRowComponent
          listingItem={listingItem}
          selectedHumanId={props.selectedHumanId}
          onOptimizationRequestClick={() => onOptimizationClick(listingItem.id)}
        />
      ),
      isHiddenComponentVisible: true,
    };
  };

  const rows: OrderOptimizerTaxiListingTableRow[] = useMemo(() => {
    return props.listingItems.map((list) => createTableRow(list));
  }, [props.listingItems, selectedAppLanguage]);

  return (
    <TableComponent
      columns={columns}
      rows={rows}
      isLoading={props.isLoading}
      isError={props.isError}
      noHeader
    />
  );
};

export default OrderOptimizerTaxiListingTableComponent;
